import { PayloadAction } from "@reduxjs/toolkit";
import toolIndex from "toolConstants";
import { getStoreProp } from "utilities/string";

export const dimensionsReducers = {
  /**
   * Sets the dimensions of an area or the container.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new dimensions.
   * @param {number} [action.payload.length] - The new length.
   * @param {number} [action.payload.width] - The new width.
   */
  setDimensions: (state: any, action: PayloadAction<{ width?: number; length?: number }>) => {
    const { length, width } = action.payload;
    const { length: currentLength, width: currentWidth } = (state.areas[state.activeTab] || state).dimensions;
    (state.areas[state.activeTab] || state).dimensions = {
      length: length || currentLength,
      width: width || currentWidth,
    };
    // Area resets - clear area stuff when dims change
    if (state.activeTab in state.areas && ((length && length !== currentLength) || (width && width !== currentWidth))) {
      // toolIndex[state.currentTool].resetContents(state)
      const parameter = getStoreProp(action.type)
      toolIndex[state.currentTool].resetArea(state, parameter)
    };
  },

  /**
   * Sets the ceiling height for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new ceiling height.
   * @param {number} [action.payload.min] - The minimum ceiling height.
   * @param {number} [action.payload.max] - The maximum ceiling height.
   */
  setCeilingHeight: (state: any, action: PayloadAction<{ min?: number; max?: number }>) => {
    if (!(state.activeTab in state.areas)) return;
    const { min, max } = action.payload;
    const area = state.areas[state.activeTab];
    const { min: currentMin } = area.ceilingHeight;

    // Determine the new value, treating 0 as a valid input that needs to be processed
    const newValue = min !== undefined ? min : (!max || currentMin < max) ? currentMin : max;

    // Check if ceiling microphone needs to follow ceiling height
    const microphones = area.microphones;
    if (microphones && microphones.model && microphones.model.includes('ceiling')) {
      const currentMicHeight = microphones.height;
      const tolerance = 0.0001;

      // If microphone is currently at ceiling height, move it with the ceiling
      if (Math.abs(currentMicHeight - currentMin) < tolerance) {
        microphones.height = newValue;
        // Clear any validation flags since we're auto-adjusting
        if (area._originalHeightInput !== undefined) {
          delete area._originalHeightInput;
        }
      }
      // If lowering ceiling below current microphone height, adjust microphone down
      else if (newValue < currentMicHeight) {
        microphones.height = newValue;
        // Clear any validation flags since we're auto-adjusting
        if (area._originalHeightInput !== undefined) {
          delete area._originalHeightInput;
        }
      }
    }

    // PIG-612: speakers must follow the ceiling like mics do, so the coverage radius — which now
    // reads the stored speaker height (see roomCalculations `speakerHeight`) — recomputes correctly
    // on a room-height change. Ceiling-mounted speakers sit AT the ceiling; pendant/wall speakers
    // hang below it and only need clamping DOWN when the new ceiling drops beneath their height.
    const equipmentSpeakers = toolIndex[state.currentTool]?.constants?.equipment?.speakers;
    const syncSpeakerToCeiling = (spk: any) => {
      if (!spk || !spk.model || typeof spk.height !== 'number') return;
      const defSpkr = equipmentSpeakers?.[spk.model];
      if (!defSpkr) return;
      let newHeight = spk.height;
      if (defSpkr.isCeilingSpkr) {
        newHeight = newValue;
      } else if ((defSpkr.isPendantSpkr || defSpkr.isWallSpkr) && newValue < spk.height) {
        const minBound = defSpkr.heightRange ? defSpkr.heightRange(area)[0] : 0;
        newHeight = Math.max(minBound, newValue);
      }
      if (newHeight !== spk.height) {
        spk.height = newHeight;
        // Keep any placed layout's z in sync so tooltips/plots match (mirrors setSpeakers).
        if (Array.isArray(spk.layout)) {
          spk.layout = spk.layout.map((s: any) => ({ ...s, location: { ...s.location, z: newHeight } }));
        }
      }
    };
    syncSpeakerToCeiling(area.speakers);
    syncSpeakerToCeiling(area.expansionSpeakers);

    // Always update state - let validation handle the error display
    area.ceilingHeight = {
      min: newValue,
      max: newValue,
    }
  },

  /**
   * Sets the mounting height for devices in the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new mounting height.
   * @param {number} [action.payload.min] - The minimum mounting height.
   * @param {number} [action.payload.max] - The maximum mounting height.
   */
  setMountingHeight: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    const { min, max } = action.payload;
    const { min: currentMin, max: currentMax } = state.areas[state.activeTab].mountingHeight;
    // NOTE: this might get simplified if we check in middleware
    state.areas[state.activeTab].mountingHeight = {
      min: Math.max(0, min ? min : (!max || currentMin < max) ? currentMin : max),
      max: Math.max(0, max ? max : (!min || currentMax > min) ? currentMax : min)
    }
  },

  /**
   * Toggles the presence of a surrounding wall for the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {string} action.payload - The wall identifier (e.g., 'top', 'right', 'bottom', 'left').
   */
  setSurroundingWalls: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    const side = action.payload.replace("wall", "");
    state.areas[state.activeTab].surroundingWalls = { ...state.areas[state.activeTab].surroundingWalls, [side]: !state.areas[state.activeTab].surroundingWalls[side] };
  },

  /**
   * Sets the location of the current area.
   * @param {Object} state - The current state.
   * @param {Object} action - The action object.
   * @param {Object} action.payload - The new location.
   * @param {number} [action.payload.left] - The new left position.
   * @param {number} [action.payload.top] - The new top position.
   */
  setLocation: (state: any, action: any) => {
    if (!(state.activeTab in state.areas)) return;
    const { left, top } = action.payload;
    const { left: currentLeft, top: currentTop } = state.areas[state.activeTab].location;
    state.areas[state.activeTab].location = {
      left: left !== undefined ? left : currentLeft,
      top: top !== undefined ? top : currentTop,
    };
  },

  /* Toggles the open ceiling status for the current area.
   * @param {Object} state - The current state.
   */
  setOpenCeiling: (state: any, action: any) => {
    const currentArea = state.areas[state.activeTab];
    currentArea.openCeiling = !currentArea.openCeiling
    if (currentArea.openCeiling) {
      currentArea.ceilingHeight = {
        min: state.roofHeight.min,
        max: state.roofHeight.min
      };
      currentArea.materials.ceiling = state.materials.ceiling
    } else {
      currentArea.snapToGrid = false
    };
  },

  /**
   * Toggles the snap to grid status for the current area.
   * @param {Object} state - The current state.
   */
  setSnapToGrid: (state: any, action: any) => {
    state.areas[state.activeTab].snapToGrid = !state.areas[state.activeTab].snapToGrid
  },

  /**
   * Resets dimensions to default values based on tool constants.
   * @param {Object} state - The current state.
   */
  resetDimensionsToDefault: (state: any) => {
    // Get default dimensions from tool constants
    const defaultDimensions = toolIndex[state.currentTool]?.initialState?.area(state)?.dimensions;
    if (defaultDimensions && state.areas[state.activeTab]) {
      state.areas[state.activeTab].dimensions = { ...defaultDimensions };
    }
  },

  /**
   * Resets ceiling height to default values based on tool constants.
   * @param {Object} state - The current state.
   */
  resetCeilingHeightToDefault: (state: any) => {
    // Get default ceiling height from tool constants
    const defaultCeilingHeight = toolIndex[state.currentTool]?.initialState?.area(state)?.ceilingHeight;
    if (defaultCeilingHeight && state.areas[state.activeTab]) {
      state.areas[state.activeTab].ceilingHeight = { ...defaultCeilingHeight };
    }
  },
};
